/* eslint-disable global-require */
import React, { useEffect, useState } from 'react'
import InputMask from 'react-input-mask'
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Alert,
} from 'reactstrap'
import { useForm, Controller, ErrorMessage } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import NotificationAlert from 'react-notification-alert'
import Background from '../../assets/img/wic/Fruits_slides.jpg'
import { useFormUtils } from 'views/shared/useFormUtils'

const PUBLIC_USER = 'Public'
const MANUFACTURER_USER = 'Manufacturer'
const VENDOR_USER = 'Vendor'
const apiUrl = window.config.apiUrl

const formatRegistrationInfo = data => {
  const plainOfficePhone = data.officePhone.replace(/\D/g, '')
  const plainCellPhone = data.cellPhone.replace(/\D/g, '')
  return {
    userType: data.userType,
    fName: data.firstName.trim(),
    lName: data.lastName.trim(),
    conEmail: data.email.trim(),
    conOfficePh: plainOfficePhone,
    conCellPh: plainCellPhone,
    vendorName: data.vendorName?.trim() || null,
    vendorStreet: data.vendorStreet.trim(),
    vendorCity: data.vendorCity.trim(),
    vendorState: data.vendorState.trim().toUpperCase(),
    vendorZip: data.vendorZip.trim(),
  }
}

const sleep = milliseconds => {
  return new Promise(resolve => setTimeout(resolve, milliseconds))
}

const Register = () => {
  const [userType, setUserType] = useState(MANUFACTURER_USER)
  const [submitting, setSubmitting] = useState(false)

  const history = useHistory()
  const { asterisk } = useFormUtils()

  useEffect(() => {
    document.body.classList.toggle('register-page')
    return () => {
      document.body.classList.toggle('register-page')
    }
  }, [history])

  const handleCancelClick = e => {
    e.preventDefault()
    history.push('/')
  }

  const notificationAlert = React.useRef()
  const notify = (message, messageType) => {
    let options = {
      place: 'bc',
      message: (
        <div>
          <h5>{message}</h5>
        </div>
      ),
      type: messageType,
      icon: 'nc-icon nc-bell-55',
      autoDismiss: 5,
    }
    notificationAlert.current.notificationAlert(options)
  }

  const onFormSubmit = async data => {
    if (submitting) return

    setSubmitting(true)
    notify(`Registering user: ${data.firstName} ${data.lastName}`, 'info')

    try {
      const response = await axios.post(
        `${apiUrl}/contacts/addnewcontact`,
        formatRegistrationInfo(data)
      )

      if (response.status === 202) {
        notify(
          'Your registration status could not be confirmed. Contact APLSS support before trying again.',
          'warning'
        )
      } else {
        notify(
          'Congratulations, you have been registered in our system. Check your inbox for an email to complete your APLSS registration.',
          'info'
        )
      }

      await sleep(5000)
      history.push('/')
    } catch (error) {
      if (
        error.response?.status === 409 ||
        error.response?.data?.code === 'EMAIL_EXISTS'
      ) {
        notify('Email already exists.', 'warning')
      } else if (error.response?.status === 429) {
        notify(
          'Too many registration attempts. Please try again later.',
          'warning'
        )
      } else {
        notify(
          'Sorry, we are unable to process your registration at this time.',
          'danger'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  const {
    register,
    errors,
    handleSubmit,
    watch,

    control,
  } = useForm()
  const publicUser = watch('userType', 'Manufacturer')

  return (
    <div className="register-page">
      <Container>
        <NotificationAlert ref={notificationAlert} />
        <Row>
          <Col className="ml-auto mr-auto" lg="8" md="10">
            <Form className="form" onSubmit={handleSubmit(onFormSubmit)}>
              <Card className="card-signup text-center">
                <CardHeader>
                  <CardTitle tag="h4">Register</CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col className="pr-1" md="6">
                      <FormGroup>
                        <Label>First Name {asterisk}</Label>
                        <Input
                          name="firstName"
                          innerRef={register({
                            required: '⚠ First Name is required',
                          })}
                        />
                      </FormGroup>
                      {errors.firstName && (
                        <p className="text-danger">
                          {errors.firstName.message}
                        </p>
                      )}
                    </Col>
                    <Col className="pl-1" md="6">
                      <FormGroup>
                        <Label>Last Name {asterisk}</Label>
                        <Input
                          name="lastName"
                          innerRef={register({
                            required: '⚠ Last Name is required',
                          })}
                        />
                      </FormGroup>
                      {errors.lastName && (
                        <p className="text-danger">{errors.lastName.message}</p>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-1" md="4">
                      <FormGroup>
                        <Label>User Type</Label>
                        <Input
                          type="select"
                          name="userType"
                          innerRef={register}
                          onChange={e => {
                            setUserType(e.target.value)
                          }}
                        >
                          <option>{MANUFACTURER_USER}</option>
                          <option>{VENDOR_USER}</option>
                          <option>{PUBLIC_USER}</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col className="pr-1" md="8">
                      <FormGroup>
                        <Label>
                          Company Name{' '}
                          {userType !== PUBLIC_USER ? asterisk : null}
                        </Label>
                        <Input
                          name="vendorName"
                          innerRef={register({
                            required: publicUser !== 'Public' ? true : false,
                          })}
                          disabled={publicUser === 'Public'}
                        />
                      </FormGroup>
                      {errors.vendorName && (
                        <p className="text-danger">
                          ⚠ Company Name is required
                        </p>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-1" md="4">
                      <FormGroup>
                        <Label>Primary Phone {asterisk}</Label>
                        <Controller
                          as={Input}
                          name="officePhone"
                          control={control}
                          mask="(999) 999-9999"
                          tag={InputMask}
                          defaultValue=""
                          rules={{
                            pattern: {
                              value: /([(][0-9]{3}[)][ ][0-9]{3}[-][0-9]{4})/,
                              message: '⚠ Invalid phone number',
                            },
                            required: {
                              value: true,
                              message: '⚠ Phone number is required.',
                            },
                          }}
                        />
                      </FormGroup>
                      <p className={'text-danger'}>
                        <ErrorMessage
                          errors={errors}
                          name="officePhone"
                          as={<Alert color={'danger'} className={'mt-3'} />}
                        />
                      </p>
                    </Col>
                    <Col className="pr-1" md="4">
                      <FormGroup>
                        <Label>Secondary Phone</Label>
                        <Controller
                          as={Input}
                          name="cellPhone"
                          control={control}
                          mask="(999) 999-9999"
                          tag={InputMask}
                          defaultValue=""
                          rules={{
                            pattern: /([(][0-9]{3}[)][ ][0-9]{3}[-][0-9]{4})/,
                          }}
                        />
                        <p className={'text-danger'}>
                          <ErrorMessage
                            errors={errors}
                            name="cellPhone"
                            message={'⚠ Invalid phone number.'}
                            as={<Alert color={'danger'} className={'mt-3'} />}
                          />
                        </p>
                      </FormGroup>
                    </Col>
                    <Col className="pl-1" md="4">
                      <FormGroup>
                        <Label>Email address {asterisk}</Label>
                        <Input
                          name="email"
                          innerRef={register({
                            required: '⚠ Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                              message: '⚠ Invalid email address',
                            },
                          })}
                        />
                      </FormGroup>
                      <p className={'text-danger'}>
                        <ErrorMessage
                          errors={errors}
                          name="email"
                          as={<Alert color={'danger'} className={'mt-3'} />}
                        />
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup>
                        <Label>Address {asterisk}</Label>
                        <Input
                          name="vendorStreet"
                          innerRef={register({
                            required: '⚠ Address is required',
                            maxLength: 50,
                          })}
                        />
                      </FormGroup>
                      {errors.vendorStreet && (
                        <p className="text-danger">
                          {errors.vendorStreet.message}
                        </p>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-1" md="7">
                      <FormGroup>
                        <Label>City {asterisk}</Label>
                        <Input
                          name="vendorCity"
                          innerRef={register({
                            required: '⚠ City is required',
                            maxLength: 25,
                          })}
                        />
                      </FormGroup>
                      {errors.vendorCity && (
                        <p className="text-danger">
                          {errors.vendorCity.message}
                        </p>
                      )}
                    </Col>
                    <Col className="px-1" md="2">
                      <FormGroup>
                        <Label>State {asterisk}</Label>
                        <Input
                          name="vendorState"
                          innerRef={register({
                            required: '⚠ State is required',
                            maxLength: {
                              value: 2,
                              message: '⚠ State should be 2 characters',
                            },
                            pattern: {
                              value: /^(?:(A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|P[AR]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY]))$/i,
                              message: 'Invalid State',
                            },
                          })}
                        />
                      </FormGroup>
                      {errors.vendorState && (
                        <p className="text-danger">
                          {errors.vendorState.message}
                        </p>
                      )}
                    </Col>
                    <Col className="pl-1" md="3">
                      <FormGroup>
                        <Label>Zip Code {asterisk}</Label>
                        <Input
                          name="vendorZip"
                          id="vendorZip"
                          placeholder="00000"
                          type="numeric"
                          innerRef={register({
                            required: '⚠ Zip Code is required',
                            pattern: /^\d{5}?$/i,
                            minLength: 5,
                            maxLength: 5,
                          })}
                        />
                      </FormGroup>
                      {errors.vendorZip?.type === 'minLength' && (
                        <p className="text-danger">
                          ⚠ Zipcode minimum length is 5 digits
                        </p>
                      )}
                      {errors.vendorZip?.type === 'maxLength' && (
                        <p className="text-danger">
                          ⚠ Zipcode maximum length is 5 digits
                        </p>
                      )}
                      {errors.vendorZip && (
                        <p className="text-danger">
                          {errors.vendorZip.message}
                        </p>
                      )}
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter>
                  <Button
                    className="btn-round mr-2"
                    color="info"
                    disabled={submitting}
                  >
                    {submitting ? 'Registering...' : 'Register'}
                  </Button>
                  <Button
                    className="btn-round ml-2"
                    color="danger"
                    href="#pablo"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                </CardFooter>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
      <div
        className="full-page-background"
        style={{
          backgroundImage: `url(${Background})`,
          opacity: 0.4,
        }}
      />
    </div>
  )
}

export default Register
